import type { ReactNode } from 'react';
import { Carousel } from '@mantine/carousel';
import { Box, Stack, Text, Title } from '@mantine/core';

export type HeroSlide = {
  image: string;
  /** object-position фона, по умолчанию 'center' */
  imagePosition?: string;
  title?: string;
  /** крупная акцентная строка под title (имя клиента) */
  titleAccent?: string;
  subtitle?: string;
  cta?: ReactNode;
};

/**
 * Карусель hero-баннеров. Сейчас слайд один (welcome) — индикаторы и свайп
 * включаются автоматически, когда слайдов станет больше (Artem пришлёт фото).
 */
export function HeroCarousel({ slides, isDesktop }: { slides: HeroSlide[]; isDesktop: boolean }) {
  const many = slides.length > 1;
  // Десктоп: hero подстраивается под высоту вьюпорта, чтобы главная (включая
  // нижние карточки и футер) помещалась без прокрутки. 720px — суммарная высота
  // остального контента в компактном режиме (шапка + меню/новости + статистика
  // + футер + отступы); правишь вёрстку ниже hero — сверь эту константу.
  const height = isDesktop ? 'clamp(170px, calc(100vh - 720px), 320px)' : 460;

  const slide = (s: HeroSlide, i: number) => (
    <Carousel.Slide key={i}>
      <Box style={{ position: 'relative', height, borderRadius: 16, overflow: 'hidden' }}>
        <img
          src={s.image}
          alt=""
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: s.imagePosition ?? 'center',
          }}
        />
        {/* затемнение слева для читаемости текста */}
        <Box
          style={{
            position: 'absolute', inset: 0,
            background: isDesktop
              ? 'linear-gradient(90deg, rgb(6 26 26 / 82%) 0%, rgb(6 26 26 / 45%) 45%, rgb(6 26 26 / 5%) 75%)'
              : 'linear-gradient(180deg, rgb(6 26 26 / 30%) 0%, rgb(6 26 26 / 72%) 100%)',
          }}
        />
        <Stack
          gap={8}
          justify="center"
          style={{
            position: 'absolute', inset: 0,
            padding: isDesktop ? '0 56px' : '24px 20px',
            maxWidth: isDesktop ? 620 : undefined,
          }}
        >
          {s.title && <Text c="#fff" fz={isDesktop ? 20 : 18} lh={1.3}>{s.title}</Text>}
          {s.titleAccent && (
            <Title order={2} c="#fff" fz={isDesktop ? 44 : 34} fw={850} lh={1.05} style={{ letterSpacing: '-0.02em' }}>
              {s.titleAccent}
            </Title>
          )}
          {s.subtitle && (
            <Text c="rgb(255 255 255 / 85%)" fz={isDesktop ? 16 : 15} lh={1.5} maw={420}>
              {s.subtitle}
            </Text>
          )}
          {s.cta && <Box mt={10}>{s.cta}</Box>}
        </Stack>
      </Box>
    </Carousel.Slide>
  );

  return (
    <Carousel
      withControls={false}
      withIndicators={many}
      {...(many ? {} : { draggable: false })}
      styles={{ indicator: { background: '#fff', width: 8, height: 8 } }}
    >
      {slides.map(slide)}
    </Carousel>
  );
}
